<script setup lang="ts">
/**
 * @changelog
 *
 * 2026-09-14 (0.1.0) (jhoward)
 *   - Add MaintenanceWindowBanner.vue, the guest-facing warning that scheduled maintenance is coming.
 */

import { computed, nextTick, useTemplateRef, watch } from 'vue';
import useMaintenanceWindow from '@/helpers/useMaintenanceWindow';

const { window: maintenanceWindow, phase, isVisible, isSticky, remainingSeconds, dismiss } = useMaintenanceWindow();

const container = useTemplateRef<HTMLElement | null>('container');

/** mm:ss while the countdown is running. Shown to sighted users only - see the live-region note below. */
const countdown = computed<string>(() => {
  const remaining = remainingSeconds.value;
  if (null === remaining || remaining <= 0) {
    return '0:00';
  }

  const minutes = Math.floor(remaining / 60);
  const seconds = remaining % 60;

  return `${minutes}:${String(seconds).padStart(2, '0')}`;
});

/**
 * The announced sentence, deliberately coarse.
 *
 * It only changes on the whole minute, which is what keeps a screen reader from reading the clock out every second
 * while it ticks. The digits themselves live in an aria-hidden node.
 */
const spokenSummary = computed<string>(() => {
  if ('started' === phase.value) {
    return 'Scheduled maintenance is beginning. The site may become unavailable at any moment.';
  }

  const remaining = remainingSeconds.value ?? 0;
  const minutes = Math.max(1, Math.ceil(remaining / 60));

  return `Scheduled maintenance begins in ${minutes} ${1 === minutes ? 'minute' : 'minutes'}.`;
});

const headline = computed<string>(() => {
  if ('started' === phase.value) {
    return 'Maintenance is beginning';
  }

  return 'Scheduled maintenance';
});

const detail = computed<string>(() => {
  if ('started' === phase.value) {
    return 'The site may become unavailable at any moment. Please finish up and save your work.';
  }

  if ('sticky' === phase.value) {
    return 'Please finish what you are doing and save your work.';
  }

  const remaining = remainingSeconds.value ?? 0;
  const minutes = Math.max(1, Math.round(remaining / 60));

  return `This site will be unavailable for maintenance in about ${minutes} ${1 === minutes ? 'minute' : 'minutes'}.`;
});

/**
 * Focus rescue, and nothing more.
 *
 * When the banner escalates, the dismiss button is removed. If the guest happened to have it focused, focus would
 * fall to <body> and their place in the page would be gone - so it is moved to the container instead. The banner
 * never takes focus otherwise: it is a warning, not a dialog, and pulling the caret out of whatever someone is
 * typing would be its own bug.
 */
watch(isSticky, async (sticky) => {
  if (!sticky) {
    return;
  }

  const active = document.activeElement;
  if (active && container.value?.contains(active)) {
    await nextTick();
    container.value?.focus({ preventScroll: true });
  }
});
</script>

<template>
  <transition name="maintenance_banner--slide">
    <!--
      Keyed on the phase so Vue tears the element down and rebuilds it when the banner escalates. Screen readers
      bind a live region at insertion time and do not reliably notice role/aria-live changing on a node that is
      already mounted, so swapping the attribute in place would announce nothing.
    -->
    <section
      v-if="isVisible && maintenanceWindow"
      :key="phase"
      ref="container"
      tabindex="-1"
      class="maintenance_banner--container"
      :class="{ 'maintenance_banner--container-sticky': isSticky }"
      :role="isSticky ? 'alert' : 'status'"
    >
      <div class="maintenance_banner--grid">
        <span class="material-icons maintenance_banner--icon" aria-hidden="true">
          {{ isSticky ? 'warning' : 'schedule' }}
        </span>

        <div>
          <p class="maintenance_banner--headline">
            {{ headline }}
            <span v-if="isSticky && 'started' !== phase" class="maintenance_banner--countdown" aria-hidden="true">
              {{ countdown }}
            </span>
          </p>
          <!-- Never v-html: the message is administrator-authored but renders to every signed-out visitor. -->
          <p class="maintenance_banner--detail">{{ maintenanceWindow.message || detail }}</p>
          <p v-if="maintenanceWindow.message" class="maintenance_banner--detail">{{ detail }}</p>

          <!-- What is actually announced. Changes only on the whole minute, so the clock above is not read out. -->
          <p class="maintenance_banner--spoken">{{ spokenSummary }}</p>
        </div>

        <button v-if="!isSticky" type="button" class="maintenance_banner--dismiss" @click="dismiss">Dismiss</button>
      </div>
    </section>
  </transition>
</template>

<style scoped>
.maintenance_banner--container {
  position: fixed;
  /*
   * Stated rather than left to the default, so nothing can leave this element transparent. See the transition
   * below: a warning that is invisible is worse than a warning that does not slide.
   */
  opacity: 1;
  /*
   * Bottom-fixed, following CookieDisclosure. AppHeader is fixed at the top with a 200px height against only 4rem
   * of padding on .body--container, so a top banner would sit underneath it or have to hardcode that height.
   *
   * The custom property is what keeps the two bottom-fixed banners off each other: App.vue raises it when the
   * cookie disclosure is enabled. They do not collide today only because that flag is hardcoded false.
   */
  bottom: var(--maintenance-banner-bottom, 1em);
  left: 0;
  right: 0;
  /* See the z-index register at the top of src/main.css before changing this. */
  z-index: 145;
  margin: 0 1em;
  outline: none;
}

.maintenance_banner--grid {
  display: grid;
  grid-template-columns: max-content 1fr max-content;
  align-items: start;
  grid-gap: 0.75em;
  max-width: 800px;
  padding: 0.75em 1em;
  margin: 0 auto;
  color: #fff;
  /* Material Amber 900 - 5.2:1 on white, and it is never the only signal: the icon and the wording change too. */
  background-color: rgb(var(--status-warning, 138, 97, 0));
  box-shadow: 0 0 32px rgba(0, 0, 0, 0.54);
  border-radius: 0.25em;
}

.maintenance_banner--container-sticky .maintenance_banner--grid {
  background-color: rgb(var(--status-danger, 198, 40, 40));
}

.maintenance_banner--icon {
  font-size: 1.5rem;
  line-height: 1.4;
}

.maintenance_banner--headline {
  margin: 0;
  font-weight: 700;
}

.maintenance_banner--countdown {
  margin-left: 0.5em;
  font-variant-numeric: tabular-nums;
}

.maintenance_banner--detail {
  margin: 0.25em 0 0 0;
  font-size: 0.9rem;
}

/* Announced, not shown. The visible countdown is aria-hidden so it is not read out on every tick. */
.maintenance_banner--spoken {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.maintenance_banner--dismiss {
  align-self: center;
  padding: 0.5em 0.75em;
  color: #fff;
  font: inherit;
  text-transform: uppercase;
  background: rgba(255, 255, 255, 0.16);
  border: 1px solid rgba(255, 255, 255, 0.6);
  border-radius: 0.25em;
  cursor: pointer;
  white-space: nowrap;
  transition: background-color 150ms cubic-bezier(0.4, 0, 0.2, 1);
}

.maintenance_banner--dismiss:hover,
.maintenance_banner--dismiss:focus {
  background: rgba(255, 255, 255, 0.28);
}

/*
 * Transform only - opacity is deliberately not animated.
 *
 * Vue drives an enter transition off requestAnimationFrame, and rAF does not run in a background tab. An element
 * inserted while the tab is hidden therefore sits on the enter-from class until the tab is looked at again. If
 * enter-from carried `opacity: 0`, a guest coming back to a backgrounded tab could find this warning invisible -
 * which is the one thing it must never be. Stalled on `translateY(1em)` it is merely a few pixels low.
 */
.maintenance_banner--slide-enter-active,
.maintenance_banner--slide-leave-active {
  transition: transform 150ms cubic-bezier(0.4, 0, 0.2, 1);
}

.maintenance_banner--slide-enter-from,
.maintenance_banner--slide-leave-to {
  transform: translateY(1em);
}

@media (max-width: 800px) {
  .maintenance_banner--grid {
    border-radius: initial;
  }
}

@media (max-width: 500px) {
  .maintenance_banner--grid {
    grid-template-columns: max-content 1fr;
  }

  .maintenance_banner--dismiss {
    grid-column: 2;
    justify-self: start;
  }
}

@media (prefers-reduced-motion: reduce) {
  .maintenance_banner--slide-enter-active,
  .maintenance_banner--slide-leave-active {
    transition: none;
  }

  .maintenance_banner--slide-enter-from,
  .maintenance_banner--slide-leave-to {
    transform: none;
  }
}
</style>
